/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { IconButton } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import styles from "../QuoteMultipleDestinationsPanel.module.css";
import CustomerFilterArrowIcon from "../../../svg/selectRailStationCustomerDownArrow.svg";
import { XIcon, WarningIconSCRS } from "../../../icons";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { useNitroStore } from "../../../store/Store";
import {
  convertModeParams,
  normalizeDestinationName,
} from "./multiLocationQuoteUtils";
import NitroService from "../../../utils/nitro.service";
import {
  ISCRSRecord,
  IRailStationSearchResult,
} from "../../mobile/railStationLookupPanel/IRailStationLookup";
import IOSSwitch from "../../../components/IOSSwitch";
import NitroFilter from "../../../components/NitroFilter";
import { toModes } from "../../../utils/utils";
import { ICarrier } from "../../../types/ILocation";

// TODO: lines on dropdown options?
// TODO: dropdown goes over buttons
// TODO: error for rail update failure
// TODO: rows might have same id
// TODO: have claude go thru and clean up unit tests
// TODO: when we turn off a mode should the selected id on the destination get reset?
// TODO: going back from select-radius makes records appear. search is also running auto
// TODO: this panel is used for more than just destinations
// TODO: scrs carrier being toggled off is not saved to carrier selection screen

type CarrierRow = {
  name: string;
  selected: boolean;
  addedByUser?: boolean;
  createdBy?: string;
};

const SelectRailStationPanel: React.FC = () => {
  const auth = useAuth();
  const [
    setMultiLocationDestinations,
    showInsightsPanel,
    showAddLocationPanel,
    setPendingRailStationUpdate,
  ] = useNitroStore((state) => [
    state.setMultiLocationDestinations,
    state.showInsightsPanel,
    state.showAddLocationPanel,
    state.setPendingRailStationUpdate,
  ]);
  const triggerRefetchQuotes = useNitroCAStore(
    (state) => state.triggerRefetchQuotes,
  );
  const [
    setDisplayAddInboundRailPanel,
    destination,
    setDisplayInboundModeTogglePanel,
    setExpandedDestination,
  ] = useNitroCAStore((state) => [
    state.setDisplayAddInboundRailPanel,
    state.destinationToTroubleshoot,
    state.setDisplayInboundModeTogglePanel,
    state.setExpandedDestination,
  ]);
  const [selectedStationId, setSelectedStationId] = useState<string>("");
  const [customerFilterText, setCustomerFilterText] = useState<string>("");
  const [stationNameFilterText, setStationNameFilterText] =
    useState<string>("");
  const [streetAddressFilterText, setStreetAddressFilterText] =
    useState<string>("");
  const destinationCityState =
    destination.city && destination.stateOrProvince
      ? `${normalizeDestinationName(
          destination.city,
        )}, ${destination.stateOrProvince.toUpperCase()}`
      : normalizeDestinationName(destination.name);
  const [cityStateFilterText, setCityStateFilterText] =
    useState<string>(destinationCityState);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [page, setPage] = useState<number>(0);
  const [step, setStep] = useState<
    "select-station-scrs" | "select-station-radius" | "review-carriers"
  >("select-station-scrs");
  const [stationRecords, setStationRecords] = useState<ISCRSRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterErrors, setFilterErrors] = useState<{
    customer: boolean;
    stationName: boolean;
    streetAddress: boolean;
    cityState: boolean;
  }>({
    customer: false,
    stationName: false,
    streetAddress: false,
    cityState: false,
  });
  const [openRadiusDropdown, setOpenRadiusDropdown] = useState<
    "stationName" | "city" | "state" | "zipCode" | null
  >(null);

  const [carriers, setCarriers] = useState<CarrierRow[]>([]);
  // const [deletedCarriers, setDeletedCarriers] = useState<string[]>([]);
  const [selectedMissingCarrier, setSelectedMissingCarrier] = useState<
    string | null
  >(null);
  const [isMissingCarrierDropdownOpen, setIsMissingCarrierDropdownOpen] =
    useState<boolean>(false);
  const [missingCarrierSearch, setMissingCarrierSearch] = useState("");
  const [allCarrierOptions, setAllCarrierOptions] = useState<string[]>([]);

  useEffect(() => {
    NitroService.getCarriers().then((results) => {
      setAllCarrierOptions(results.map((r) => r.carrier));
    });
  }, []);

  const [radiusRecords, setRadiusRecords] = useState<
    IRailStationSearchResult[]
  >([]);
  const [radiusStationNameFilter, setRadiusStationNameFilter] = useState("");
  const [radiusCityFilter, setRadiusCityFilter] = useState("");
  const [radiusStateFilter, setRadiusStateFilter] = useState("");
  const [radiusZipcodeFilter, setRadiusZipcodeFilter] = useState("");
  const [radiusPage, setRadiusPage] = useState(0);
  const [radiusRowsPerPage, setRadiusRowsPerPage] = useState(25);
  const [selectedRadiusId, setSelectedRadiusId] = useState("");
  const [previousStep, setPreviousStep] = useState<
    "select-station-scrs" | "select-station-radius"
  >("select-station-scrs");

  const selectedStation =
    stationRecords.find((row) => row.scrs.id === selectedStationId) ?? null;

  const selectedRadiusStation =
    radiusRecords.find((row) => row.id === selectedRadiusId) ?? null;

  // how many of the four filter fields are currently non-empty
  const filledFilterCount = [
    customerFilterText.trim(),
    stationNameFilterText.trim(),
    streetAddressFilterText.trim(),
    cityStateFilterText.trim(),
  ].filter(Boolean).length;

  // rows come directly from the last Search API call — no in-memory filtering
  const stationRows = stationRecords;

  // radius dropdown filters are applied in-memory on the records returned by the API
  const filteredRadiusRows = radiusRecords
    .filter(
      (row) =>
        !radiusStationNameFilter ||
        row.name.toLowerCase().includes(radiusStationNameFilter.toLowerCase()),
    )
    .filter(
      (row) =>
        !radiusCityFilter ||
        row.city.toLowerCase().includes(radiusCityFilter.toLowerCase()),
    )
    .filter(
      (row) =>
        !radiusStateFilter ||
        row.state.toLowerCase().includes(radiusStateFilter.toLowerCase()),
    )
    .filter(
      (row) =>
        !radiusZipcodeFilter || row.zipcode.includes(radiusZipcodeFilter),
    );

  const MIN_CHARS: Record<
    "customer" | "stationName" | "streetAddress" | "cityState",
    number
  > = {
    customer: 2,
    stationName: 2,
    streetAddress: 3,
    cityState: 2,
  };

  const radiusStationNameOptions = useMemo(
    () => [...new Set(radiusRecords.map((r) => r.name))].sort(),
    [radiusRecords],
  );
  const radiusCityOptions = useMemo(
    () => [...new Set(radiusRecords.map((r) => r.city))].sort(),
    [radiusRecords],
  );
  const radiusStateOptions = useMemo(
    () => [...new Set(radiusRecords.map((r) => r.state))].sort(),
    [radiusRecords],
  );
  const radiusZipcodeOptions = useMemo(
    () => [...new Set(radiusRecords.map((r) => r.zipcode))].sort(),
    [radiusRecords],
  );

  const handleRadiusSearch = async () => {
    setLoading(true);
    try {
      const records = await NitroService.getRailStationsByZipCode({
        zipCode: destination.details?.zipCode ?? "",
      });
      setRadiusRecords(records);
      setStep("select-station-radius");
    } finally {
      setLoading(false);
    }
  };

  const handleRadiusClear = () => {
    setRadiusStationNameFilter("");
    setRadiusCityFilter("");
    setRadiusStateFilter("");
    setRadiusZipcodeFilter("");
    setRadiusPage(0);
    setSelectedRadiusId("");
    setOpenRadiusDropdown(null);
    setRadiusRecords([]);
  };

  const handleSCRSSearch = async () => {
    if (filledFilterCount === 0) return;

    const errors = {
      customer:
        customerFilterText.trim().length > 0 &&
        customerFilterText.trim().length < MIN_CHARS.customer,
      stationName:
        stationNameFilterText.trim().length > 0 &&
        stationNameFilterText.trim().length < MIN_CHARS.stationName,
      streetAddress:
        streetAddressFilterText.trim().length > 0 &&
        streetAddressFilterText.trim().length < MIN_CHARS.streetAddress,
      cityState:
        cityStateFilterText.trim().length > 0 &&
        cityStateFilterText.trim().length < MIN_CHARS.cityState,
    };
    setFilterErrors(errors);

    const anyError =
      errors.customer ||
      errors.stationName ||
      errors.streetAddress ||
      errors.cityState;
    if (anyError) return;

    setSelectedStationId("");
    setLoading(true);
    try {
      const records = await NitroService.getSCRSRailData({
        cityState: cityStateFilterText.trim(),
        stationState: stationNameFilterText.trim(),
        customerName: customerFilterText.trim(),
        address: streetAddressFilterText.trim(),
      });
      setStationRecords(records);
      if (destination.details?.scrsId && !selectedStationId) {
        const match = records.find(
          (r) => r.scrs.id === destination.details?.scrsId,
        );
        if (match) setSelectedStationId(match.scrs.id);
      }
    } finally {
      setLoading(false);
    }
  };

  // pagination calculations
  const totalRows = stationRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  useEffect(() => {
    if (page >= totalPages) setPage(totalPages - 1);
  }, [page, totalPages]);

  const radiusTotalRows = filteredRadiusRows.length;
  const radiusTotalPages = Math.max(
    1,
    Math.ceil(radiusTotalRows / radiusRowsPerPage),
  );
  useEffect(() => {
    if (radiusPage >= radiusTotalPages) setRadiusPage(radiusTotalPages - 1);
  }, [radiusPage, radiusTotalPages]);

  // fire initial search on mount using the pre-populated city/state value
  useEffect(() => {
    if (!destinationCityState.trim()) return;
    setLoading(true);
    handleSCRSSearch();
  }, []);

  // reset page and selected station when filters change;
  // also clear results when all fields are empty so the empty state is shown
  useEffect(() => {
    setPage(0);
    setSelectedStationId("");
    if (filledFilterCount === 0) {
      setStationRecords([]);
    }
  }, [
    customerFilterText,
    stationNameFilterText,
    streetAddressFilterText,
    cityStateFilterText,
  ]);
  useEffect(() => {
    setRadiusPage(0);
    setSelectedRadiusId("");
  }, [
    radiusStationNameFilter,
    radiusCityFilter,
    radiusStateFilter,
    radiusZipcodeFilter,
  ]);

  // calculate visible rows based on pagination
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
  const visibleRows = stationRows.slice(startIndex, endIndex);
  // if there are no rows, show 0-0 instead of 1-0
  const rangeStart = totalRows === 0 ? 0 : startIndex + 1;
  const rangeEnd = totalRows === 0 ? 0 : startIndex + visibleRows.length;

  const radiusStartIndex = radiusPage * radiusRowsPerPage;
  const radiusEndIndex = Math.min(
    radiusStartIndex + radiusRowsPerPage,
    radiusTotalRows,
  );
  const visibleRadiusRows = filteredRadiusRows.slice(
    radiusStartIndex,
    radiusEndIndex,
  );
  const radiusRangeStart = radiusTotalRows === 0 ? 0 : radiusStartIndex + 1;
  const radiusRangeEnd =
    radiusTotalRows === 0 ? 0 : radiusStartIndex + visibleRadiusRows.length;

  const close = () => {
    setDisplayAddInboundRailPanel(false);
    setDisplayInboundModeTogglePanel(false);
  };

  // Extracted to avoid nested ternary (eslint no-nested-ternary)
  let tableScrollContent: React.ReactNode;
  if (loading) {
    tableScrollContent = (
      <div
        className={styles.selectRailStationEmptyState}
        data-testid="select-rail-station-loading"
      >
        Loading...
      </div>
    );
  } else if (!totalRows) {
    tableScrollContent = (
      <div
        className={styles.selectRailStationEmptyState}
        data-testid="select-rail-station-empty-state"
      >
        <div className={styles.selectRailStationEmptyIconWrapper}>
          <WarningIconSCRS />
        </div>
        <p className={styles.selectRailStationEmptyHeading}>
          Looks Like We Came Up Empty
        </p>
        <p className={styles.selectRailStationEmptyBody}>
          Try adjusting your filters. If nothing appears, use the button below
          to search a broader list of stations.
        </p>
        <button
          type="button"
          className={styles.selectRailStationEmptyCta}
          data-testid="select-rail-station-empty-cta"
          onClick={handleRadiusSearch}
        >
          Search Rail Stations in a 50mi Radius
        </button>
      </div>
    );
  } else {
    tableScrollContent = (
      <table className={styles.selectRailStationTable}>
        <thead>
          <tr>
            <th>Customer</th>
            <th>Station Name</th>
            <th>Street Address</th>
            <th>City, State</th>
            <th>Zipcode</th>
            <th>Carrier</th>
            <th>Connecting</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => {
            const isSelected = selectedStationId === row.scrs.id;
            return (
              <tr
                key={row.scrs.id}
                className={
                  isSelected ? styles.selectRailStationRowSelected : ""
                }
                data-testid={`select-rail-station-row-${row.scrs.id}`}
              >
                <td>{row.scrs.customerName}</td>
                <td>{row.scrs.stationName}</td>
                <td>{row.scrs.address}</td>
                <td>{`${row.scrs.city}, ${row.scrs.state}`}</td>
                <td>{row.scrs.zip}</td>
                <td>{row.scrs.carrier}</td>
                <td>{row.scrs.connectingCarriers.join(", ")}</td>
                <td>
                  <button
                    type="button"
                    className={`${styles.selectRailStationSelectButton} ${
                      isSelected
                        ? styles.selectRailStationSelectButtonActive
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedStationId((prevSelectedId) =>
                        prevSelectedId === row.scrs.id ? "" : row.scrs.id,
                      )
                    }
                    data-testid={`select-rail-station-action-${row.scrs.id}`}
                  >
                    {isSelected ? "Selected" : "Select"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  const handleFinish = async () => {
    const stationName =
      previousStep === "select-station-scrs"
        ? selectedStation?.scrs.stationName ?? ""
        : selectedRadiusStation?.id ?? "";
    const stationState =
      previousStep === "select-station-scrs"
        ? selectedStation?.scrs.stationState ?? ""
        : selectedRadiusStation?.id ?? "";
    const selectedCarriers = carriers
      .filter((c) => c.selected)
      .map(
        (c) =>
          ({
            id: c.name,
            manuallyCreated: c.addedByUser ?? false,
            createdBy: c.createdBy ?? "",
          } as ICarrier),
      );
    const userAddedCarriers = selectedCarriers
      .filter((c) => c.manuallyCreated)
      .map((c) => c.id);
    if (userAddedCarriers.length > 0) {
      await NitroService.createCarrierForStation({
        stationState,
        carriers: userAddedCarriers,
        username: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
      });
      // TODO: add error handling
      // if (carriersCreated.length !== userAddedCarriers.length) {
      //   throw new Error("Failed to create all carriers");
      // }
    }

    let scrsId;
    let railStation: string | undefined;
    if (previousStep === "select-station-scrs") {
      scrsId = selectedStation?.scrs.id;
    } else if (previousStep === "select-station-radius") {
      scrsId = undefined;
      railStation = selectedRadiusStation?.id;
    }

    if (showInsightsPanel || showAddLocationPanel) {
      setPendingRailStationUpdate({
        locationId: destination.id,
        stationName,
        stationState,
        carriers: selectedCarriers,
        scrsId,
        targetRowId: destination.targetRowId,
      });
    } else {
      await NitroService.updateRailStation({
        id: destination.id,
        scrsId,
        stationState: railStation,
        carriers: selectedCarriers.map((c) => c.id) || [],
      });
      await NitroService.updateLocationModes2({
        id: destination.id,
        modes: [
          {
            id: "RAIL",
            inbound: true,
            outbound:
              destination.details?.outboundModes.includes("RAIL") ?? false,
          },
          ...convertModeParams(
            toModes(
              destination.details?.inboundModes || [],
              destination.details?.outboundModes || [],
            ),
          ),
        ],
        threeLegsAllowed: destination.details?.threeLegsAllowed ?? false,
        equipmentTypes: destination.details?.equipmentTypes ?? [],
        railStations: [
          {
            scrsId,
            stationState,
            carriers: selectedCarriers.map((carrier) => carrier.id),
          },
        ],
      });
    }
    setDisplayAddInboundRailPanel(false);
    setDisplayInboundModeTogglePanel(false);
    setMultiLocationDestinations([]);
    triggerRefetchQuotes();
    setExpandedDestination(
      {
        id: destination.id,
        name: destination.name,
      },
      {
        expandRailWithNoFreight: true,
        expandSurroundingLocations: destination.isSurroundingLocation,
      },
    );
  };

  return (
    <div
      id="select-rail-station-panel"
      className={styles.selectRailStationPanel}
    >
      <div className={styles.selectRailStationContainer}>
        {/* Stepper and Close Button Row */}
        <div className={styles.selectRailStationStepperRow}>
          <div className={styles.selectRailStationStepper}>
            {step !== "review-carriers" ? (
              <>
                <div className={styles.selectRailStationStepperActive} />
                <div className={styles.selectRailStationStepperInactive} />
              </>
            ) : (
              <>
                <div className={styles.selectRailStationStepperInactive} />
                <div className={styles.selectRailStationStepperActive} />
              </>
            )}
          </div>
          <IconButton
            className={styles.selectRailStationCloseButton}
            size="small"
            onClick={close}
            data-testid="select-rail-station-close"
          >
            <XIcon />
          </IconButton>
        </div>

        {/* Header */}
        <div className={styles.selectRailStationContextRow}>
          <div className={styles.selectRailStationContextDot} />
          <span className={styles.selectRailStationContextText}>
            {`ADDING RAIL STATION TO ${destination.displayName.toUpperCase()}`}
          </span>
        </div>
        {step === "select-station-scrs" && (
          <>
            <h2 className={styles.selectRailStationTitle}>
              Search and Select a Rail Station
            </h2>
            <p className={styles.selectRailStationDescription}>
              Filters default to city, state. Only one field is required. Update
              the filters if needed, then click search and select the correct
              row.
            </p>

            {/* Filters Section */}
            <div className={styles.selectRailStationFilters}>
              <div className={styles.selectRailStationFilterGrid}>
                <div className={styles.selectRailStationField}>
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      Customer
                    </span>
                    {filterErrors.customer && (
                      <span className={styles.selectRailStationFieldErrorText}>
                        {MIN_CHARS.customer} characters required
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    className={`${styles.selectRailStationFieldValue} ${
                      styles.selectRailStationTextInput
                    }${
                      filterErrors.customer
                        ? ` ${styles.selectRailStationFieldError}`
                        : ""
                    }`}
                    value={customerFilterText}
                    onChange={(event) => {
                      setCustomerFilterText(event.target.value);
                      if (filterErrors.customer)
                        setFilterErrors((prev) => ({
                          ...prev,
                          customer: false,
                        }));
                    }}
                    placeholder="Enter Customer..."
                    data-testid="select-rail-station-customer-filter"
                  />
                </div>
                <div className={styles.selectRailStationField}>
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      Station Name
                    </span>
                    {filterErrors.stationName && (
                      <span className={styles.selectRailStationFieldErrorText}>
                        {MIN_CHARS.stationName} characters required
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    className={`${styles.selectRailStationFieldValue} ${
                      styles.selectRailStationTextInput
                    }${
                      filterErrors.stationName
                        ? ` ${styles.selectRailStationFieldError}`
                        : ""
                    }`}
                    value={stationNameFilterText}
                    onChange={(event) => {
                      setStationNameFilterText(event.target.value);
                      if (filterErrors.stationName)
                        setFilterErrors((prev) => ({
                          ...prev,
                          stationName: false,
                        }));
                    }}
                    placeholder="Enter Station Name..."
                    data-testid="select-rail-station-station-name-filter"
                  />
                </div>
                <div className={styles.selectRailStationField}>
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      Street Address
                    </span>
                    {filterErrors.streetAddress && (
                      <span className={styles.selectRailStationFieldErrorText}>
                        {MIN_CHARS.streetAddress} characters required
                      </span>
                    )}
                  </div>
                  <input
                    type="text"
                    className={`${styles.selectRailStationFieldValue} ${
                      styles.selectRailStationTextInput
                    }${
                      filterErrors.streetAddress
                        ? ` ${styles.selectRailStationFieldError}`
                        : ""
                    }`}
                    value={streetAddressFilterText}
                    onChange={(event) => {
                      setStreetAddressFilterText(event.target.value);
                      if (filterErrors.streetAddress)
                        setFilterErrors((prev) => ({
                          ...prev,
                          streetAddress: false,
                        }));
                    }}
                    placeholder="Enter Address..."
                    data-testid="select-rail-station-street-address-filter"
                  />
                </div>
                <div className={styles.selectRailStationField}>
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      City, State
                    </span>
                  </div>
                  <NitroFilter
                    placeholder="Enter City/State..."
                    size="small"
                    data={{
                      getRecords: async (v: string) => {
                        const records = await NitroService.getLocationExpanded(
                          v,
                        );
                        return records
                          .filter(
                            (r) => r.type === "city" || r.type === "state",
                          )
                          .map((r) => ({
                            label: r.name || "",
                            value: r.name || "",
                          }));
                      },
                    }}
                    value={cityStateFilterText}
                    setValue={(val: string) => setCityStateFilterText(val)}
                    config={{ disableClearable: true }}
                    autocompleteOptions={{
                      filterOptions: (records: any) => records,
                    }}
                    data-testid="select-rail-station-city-state-filter"
                  />
                </div>
              </div>
              <div className={styles.selectRailStationFilterActions}>
                <button
                  type="button"
                  className={styles.selectRailStationClearButton}
                  onClick={() => {
                    setCustomerFilterText("");
                    setStationNameFilterText("");
                    setStreetAddressFilterText("");
                    setCityStateFilterText("");
                    setFilterErrors({
                      customer: false,
                      stationName: false,
                      streetAddress: false,
                      cityState: false,
                    });
                    setStationRecords([]);
                    setSelectedStationId("");
                  }}
                  data-testid="select-rail-station-clear"
                >
                  Clear
                </button>
                <button
                  type="button"
                  className={styles.selectRailStationSearchButton}
                  onClick={handleSCRSSearch}
                  disabled={filledFilterCount === 0 || loading}
                  data-testid="select-rail-station-search"
                >
                  <span>Search</span>
                </button>
              </div>
            </div>

            {/* Table Section */}
            <div className={styles.selectRailStationTableWrapper}>
              <div className={styles.selectRailStationTableScroll}>
                {tableScrollContent}
              </div>
              {/* Pagination Controls */}
              <div className={styles.selectRailStationTablePagination}>
                <div className={styles.selectRailStationRowsPerPage}>
                  <span>Rows per page:</span>
                  <select
                    className={styles.selectRailStationRowsPerPageSelect}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setPage(0);
                    }}
                    data-testid="select-rail-station-rows-per-page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <span
                  className={styles.selectRailStationPageRange}
                  data-testid="select-rail-station-page-range"
                >
                  {`${rangeStart}-${rangeEnd} of ${totalRows}`}
                </span>

                <div className={styles.selectRailStationPageButtons}>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                    disabled={page === 0}
                    data-testid="select-rail-station-prev-page"
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPage((prev) => Math.min(prev + 1, totalPages - 1))
                    }
                    disabled={page + 1 >= totalPages}
                    data-testid="select-rail-station-next-page"
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </div>
              </div>
            </div>

            {/* Next Button */}
            <div className={styles.selectRailStationFooter}>
              <button
                type="button"
                className={styles.selectRailStationNextButton}
                disabled={!selectedStationId}
                onClick={() => {
                  setCarriers([
                    ...(selectedStation?.scrs.carrier
                      ? [{ name: selectedStation.scrs.carrier, selected: true }]
                      : []),
                    ...(selectedStation?.scrs.connectingCarriers ?? []).map(
                      (name) => ({ name, selected: true }),
                    ),
                  ]);
                  setPreviousStep("select-station-scrs");
                  setStep("review-carriers");
                }}
                data-testid="select-rail-station-next"
              >
                Next
              </button>
            </div>
          </>
        )}
        {step === "select-station-radius" && (
          <>
            {/* Step: Radius Station Results */}
            <h2 className={styles.selectRailStationTitle}>
              Select a Rail Station
            </h2>
            <p className={styles.selectRailStationDescription}>
              Showing results within a 50 mile radius. Update the filters if
              needed, then select the correct row.
            </p>
            {/* Radius Filters */}
            <div className={styles.selectRailStationFilters}>
              <div className={styles.selectRailStationFilterGrid}>
                <div
                  className={`${styles.selectRailStationField} ${styles.selectRailStationDropdownField}`}
                >
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      Station Name
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.selectRailStationFieldValue} ${styles.selectRailStationDropdownFieldValue}`}
                    onClick={() =>
                      setOpenRadiusDropdown((prev) =>
                        prev === "stationName" ? null : "stationName",
                      )
                    }
                    data-testid="select-rail-radius-station-name-filter"
                  >
                    <span className={styles.selectRailStationDropdownFieldText}>
                      {radiusStationNameFilter || "All Station Names"}
                    </span>
                    <img
                      src={CustomerFilterArrowIcon}
                      alt=""
                      aria-hidden="true"
                      className={styles.selectRailStationDropdownIcon}
                    />
                  </button>
                  {openRadiusDropdown === "stationName" && (
                    <div className={styles.selectRailStationDropdownMenu}>
                      <button
                        type="button"
                        className={styles.selectRailStationDropdownOption}
                        onClick={() => {
                          setRadiusStationNameFilter("");
                          setOpenRadiusDropdown(null);
                        }}
                      >
                        All Station Names
                      </button>
                      {radiusStationNameOptions.map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={styles.selectRailStationDropdownOption}
                          onClick={() => {
                            setRadiusStationNameFilter(name);
                            setOpenRadiusDropdown(null);
                          }}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className={`${styles.selectRailStationField} ${styles.selectRailStationDropdownField}`}
                >
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      City
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.selectRailStationFieldValue} ${styles.selectRailStationDropdownFieldValue}`}
                    onClick={() =>
                      setOpenRadiusDropdown((prev) =>
                        prev === "city" ? null : "city",
                      )
                    }
                    data-testid="select-rail-radius-city-filter"
                  >
                    <span className={styles.selectRailStationDropdownFieldText}>
                      {radiusCityFilter || "All Cities"}
                    </span>
                    <img
                      src={CustomerFilterArrowIcon}
                      alt=""
                      aria-hidden="true"
                      className={styles.selectRailStationDropdownIcon}
                    />
                  </button>
                  {openRadiusDropdown === "city" && (
                    <div className={styles.selectRailStationDropdownMenu}>
                      <button
                        type="button"
                        className={styles.selectRailStationDropdownOption}
                        onClick={() => {
                          setRadiusCityFilter("");
                          setOpenRadiusDropdown(null);
                        }}
                      >
                        All Cities
                      </button>
                      {radiusCityOptions.map((city) => (
                        <button
                          key={city}
                          type="button"
                          className={styles.selectRailStationDropdownOption}
                          onClick={() => {
                            setRadiusCityFilter(city);
                            setOpenRadiusDropdown(null);
                          }}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className={`${styles.selectRailStationField} ${styles.selectRailStationDropdownField}`}
                >
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      State
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.selectRailStationFieldValue} ${styles.selectRailStationDropdownFieldValue}`}
                    onClick={() =>
                      setOpenRadiusDropdown((prev) =>
                        prev === "state" ? null : "state",
                      )
                    }
                    data-testid="select-rail-radius-state-filter"
                  >
                    <span className={styles.selectRailStationDropdownFieldText}>
                      {radiusStateFilter || "All States"}
                    </span>
                    <img
                      src={CustomerFilterArrowIcon}
                      alt=""
                      aria-hidden="true"
                      className={styles.selectRailStationDropdownIcon}
                    />
                  </button>
                  {openRadiusDropdown === "state" && (
                    <div className={styles.selectRailStationDropdownMenu}>
                      <button
                        type="button"
                        className={styles.selectRailStationDropdownOption}
                        onClick={() => {
                          setRadiusStateFilter("");
                          setOpenRadiusDropdown(null);
                        }}
                      >
                        All States
                      </button>
                      {radiusStateOptions.map((state) => (
                        <button
                          key={state}
                          type="button"
                          className={styles.selectRailStationDropdownOption}
                          onClick={() => {
                            setRadiusStateFilter(state);
                            setOpenRadiusDropdown(null);
                          }}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div
                  className={`${styles.selectRailStationField} ${styles.selectRailStationDropdownField}`}
                >
                  <div className={styles.selectRailStationFieldLabelRow}>
                    <span className={styles.selectRailStationFieldLabel}>
                      Zipcode
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`${styles.selectRailStationFieldValue} ${styles.selectRailStationDropdownFieldValue}`}
                    onClick={() =>
                      setOpenRadiusDropdown((prev) =>
                        prev === "zipCode" ? null : "zipCode",
                      )
                    }
                    data-testid="select-rail-radius-zipcode-filter"
                  >
                    <span className={styles.selectRailStationDropdownFieldText}>
                      {radiusZipcodeFilter || "All Zipcodes"}
                    </span>
                    <img
                      src={CustomerFilterArrowIcon}
                      alt=""
                      aria-hidden="true"
                      className={styles.selectRailStationDropdownIcon}
                    />
                  </button>
                  {openRadiusDropdown === "zipCode" && (
                    <div className={styles.selectRailStationDropdownMenu}>
                      <button
                        type="button"
                        className={styles.selectRailStationDropdownOption}
                        onClick={() => {
                          setRadiusZipcodeFilter("");
                          setOpenRadiusDropdown(null);
                        }}
                      >
                        All Zipcodes
                      </button>
                      {radiusZipcodeOptions.map((zip) => (
                        <button
                          key={zip}
                          type="button"
                          className={styles.selectRailStationDropdownOption}
                          onClick={() => {
                            setRadiusZipcodeFilter(zip);
                            setOpenRadiusDropdown(null);
                          }}
                        >
                          {zip}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.selectRailStationFilterActions}>
                <button
                  type="button"
                  className={styles.selectRailStationClearButton}
                  onClick={handleRadiusClear}
                  data-testid="select-rail-radius-clear"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Radius Table */}
            <div className={styles.selectRailStationTableWrapper}>
              <div className={styles.selectRailStationTableScroll}>
                {loading ? (
                  <div
                    className={styles.selectRailStationEmptyState}
                    data-testid="select-rail-radius-loading"
                  >
                    Loading...
                  </div>
                ) : (
                  <table className={styles.selectRailStationTable}>
                    <thead>
                      <tr>
                        <th>Station Name</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Zipcodes</th>
                        <th>Carriers</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRadiusRows.map((row) => {
                        const isSelected = selectedRadiusId === row.id;
                        return (
                          <tr
                            key={row.id}
                            className={
                              isSelected
                                ? styles.selectRailStationRowSelected
                                : ""
                            }
                            data-testid={`select-rail-radius-row-${row.id}`}
                          >
                            <td>{row.name}</td>
                            <td>{row.city}</td>
                            <td>{row.state}</td>
                            <td>{row.zipcodes.join(", ")}</td>
                            <td>{row.carriers.join(", ")}</td>
                            <td>
                              <button
                                type="button"
                                className={`${
                                  styles.selectRailStationSelectButton
                                } ${
                                  isSelected
                                    ? styles.selectRailStationSelectButtonActive
                                    : ""
                                }`}
                                onClick={() =>
                                  setSelectedRadiusId((prev) =>
                                    prev === row.id ? "" : row.id,
                                  )
                                }
                                data-testid={`select-rail-radius-action-${row.id}`}
                              >
                                {isSelected ? "Selected" : "Select"}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              {/* Radius Pagination */}
              <div className={styles.selectRailStationTablePagination}>
                <div className={styles.selectRailStationRowsPerPage}>
                  <span>Rows per page:</span>
                  <select
                    className={styles.selectRailStationRowsPerPageSelect}
                    value={radiusRowsPerPage}
                    onChange={(e) => {
                      setRadiusRowsPerPage(Number(e.target.value));
                      setRadiusPage(0);
                    }}
                    data-testid="select-rail-radius-rows-per-page"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <span
                  className={styles.selectRailStationPageRange}
                  data-testid="select-rail-radius-page-range"
                >
                  {`${radiusRangeStart}-${radiusRangeEnd} of ${radiusTotalRows}`}
                </span>
                <div className={styles.selectRailStationPageButtons}>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRadiusPage((prev) => Math.max(prev - 1, 0))
                    }
                    disabled={radiusPage === 0}
                    data-testid="select-rail-radius-prev-page"
                  >
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setRadiusPage((prev) =>
                        Math.min(prev + 1, radiusTotalPages - 1),
                      )
                    }
                    disabled={radiusPage + 1 >= radiusTotalPages}
                    data-testid="select-rail-radius-next-page"
                  >
                    <KeyboardArrowRightIcon />
                  </IconButton>
                </div>
              </div>
            </div>

            {/* Radius Footer */}
            <div className={styles.selectRailStationFooter}>
              <button
                type="button"
                className={styles.selectRailStationBackButton}
                onClick={() => setStep("select-station-scrs")}
                data-testid="select-rail-radius-back"
              >
                <span style={{ color: "#90A1B9" }}>←</span> Back
              </button>
              <button
                type="button"
                className={styles.selectRailStationFinishButton}
                disabled={!selectedRadiusId}
                onClick={() => {
                  setCarriers(
                    (selectedRadiusStation?.carriers ?? []).map((name) => ({
                      name,
                      selected: true,
                    })),
                  );
                  setPreviousStep("select-station-radius");
                  setStep("review-carriers");
                }}
                data-testid="select-rail-radius-next"
              >
                Next
              </button>
            </div>
          </>
        )}
        {step === "review-carriers" && (
          <>
            {/* Step 2: Review Carriers */}
            <h2
              className={styles.selectRailStationTitle}
              data-testid="select-rail-carriers-title"
            >
              {`Review Carriers for `}
              <span className={styles.selectRailStationTitleAccent}>
                {previousStep === "select-station-scrs"
                  ? selectedStation?.scrs.stationName ?? ""
                  : selectedRadiusStation?.name ?? ""}
              </span>
            </h2>
            <p className={styles.selectRailStationDescription}>
              Please review the serving and connecting carriers for this
              station. Carriers are enabled by default—toggle off any that do
              not apply.
            </p>

            {/* Fixed-height wrapper keeps "Missing a carrier?" pinned at a constant Y */}
            <div className={styles.selectRailStationCarrierCardWrapper}>
              {/* Carrier card — shrink-to-fit for ≤3 rows, fixed scrollable for >3 */}
              <div
                className={styles.selectRailStationCarrierCard}
                style={
                  carriers.length <= 3
                    ? { maxHeight: "none", height: "auto" }
                    : undefined
                }
              >
                {carriers.map((carrier) => (
                  <div
                    key={carrier.name}
                    className={styles.selectRailStationCarrierRow}
                  >
                    <span className={styles.selectRailStationCarrierLabel}>
                      {carrier.name}
                    </span>
                    <div className={styles.selectRailStationCarrierRowActions}>
                      <IOSSwitch
                        sx={{
                          "& .MuiSwitch-switchBase": {
                            "&.Mui-checked": {
                              "& + .MuiSwitch-track": {
                                backgroundColor: "#155DFC",
                              },
                            },
                          },
                        }}
                        checked={carrier.selected}
                        onChange={() =>
                          setCarriers((prev) =>
                            prev.map((c) =>
                              c.name === carrier.name
                                ? { ...c, selected: !c.selected }
                                : c,
                            ),
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Missing a carrier section */}
            <div className={styles.selectRailStationMissingSection}>
              <h3 className={styles.selectRailStationMissingTitle}>
                Missing a carrier?
              </h3>
              <p className={styles.selectRailStationMissingBody}>
                You can manually add additional serving and connecting carriers
                to this location.
              </p>
              <div className={styles.selectRailStationDropdownField}>
                <div className={styles.selectRailStationMissingInput}>
                  <input
                    type="text"
                    className={styles.selectRailStationMissingSearchInput}
                    placeholder={
                      selectedMissingCarrier ??
                      "Select carrier name (e.g., UP, BNSF)"
                    }
                    value={missingCarrierSearch}
                    data-testid="select-rail-missing-carrier-trigger"
                    onFocus={() => setIsMissingCarrierDropdownOpen(true)}
                    onBlur={() =>
                      setTimeout(
                        () => setIsMissingCarrierDropdownOpen(false),
                        150,
                      )
                    }
                    onChange={(e) => {
                      setMissingCarrierSearch(e.target.value);
                      setIsMissingCarrierDropdownOpen(true);
                    }}
                  />
                  <img
                    src={CustomerFilterArrowIcon}
                    alt=""
                    aria-hidden="true"
                    className={styles.selectRailStationDropdownIcon}
                  />
                </div>
                {isMissingCarrierDropdownOpen ? (
                  <div
                    className={styles.selectRailStationDropdownMenu}
                    data-testid="select-rail-missing-carrier-options"
                  >
                    {allCarrierOptions
                      .filter(
                        (name) =>
                          !carriers.some((c) => c.name === name) &&
                          name
                            .toLowerCase()
                            .includes(missingCarrierSearch.toLowerCase()),
                      )
                      .map((name) => (
                        <button
                          key={name}
                          type="button"
                          className={styles.selectRailStationDropdownOption}
                          // keep focus on the input so onBlur doesn't close the
                          // menu before this click is registered
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setCarriers((prev) => [
                              ...prev,
                              {
                                name,
                                selected: true,
                                addedByUser: true,
                                createdBy: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
                              },
                            ]);
                            setSelectedMissingCarrier(null);
                            setMissingCarrierSearch("");
                            setIsMissingCarrierDropdownOpen(false);
                          }}
                        >
                          {name}
                        </button>
                      ))}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Step 2 Footer */}
            <div className={styles.selectRailStationStep2Footer}>
              <button
                type="button"
                className={styles.selectRailStationBackButton}
                onClick={() => setStep(previousStep)}
                data-testid="select-rail-station-back"
              >
                <span style={{ color: "#90A1B9" }}>←</span> Back
              </button>
              <button
                id="add-inbound-rail-finish-button"
                type="button"
                className={styles.selectRailStationFinishButton}
                data-testid="select-rail-station-finish"
                onClick={handleFinish}
                disabled={carriers.every((c) => !c.selected)}
              >
                Finish
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectRailStationPanel;
